import { createReducer, createSelector } from '@reduxjs/toolkit';
import { all, call, fork, takeEvery } from 'redux-saga/effects';
import { CITYAREA_INCLUDE } from '../../constants/api';
import {
  createCityAreaInitialize,
  createCityAreaSuccess,
  deleteCityAreaInitialize,
  deleteCityAreaSuccess,
  getAllHearingsSuccess,
  getAllCityAreasError,
  getAllCityAreasInitialize,
  getAllCityAreasSuccess,
  getPaginatedHearingsSuccess,
  updateCityAreaInitialize,
  updateCityAreaSuccess,
  getHearingSuccess,
} from '../actions/api';
import { CityAreaConnector } from '../connectors/cityAreaConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateGetAllApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getInitActionPayload,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import {
  allHearingsSelector,
  hearingByIdSelector,
  hearingsByIdsSelector,
} from './hearingSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /CityArea
//  - GET /CityArea/{id}
//  - POST /CityArea
//  - PATCH /CityArea/{id}
//  - DELETE /CityArea/{id}
//
// CityArea is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}

// -------- Reducer --------
const cityAreaReducer = createReducer(
  {
    all: [],
    isFetchingCityAreas: false,
  },
  (builder) => {
    builder
      .addCase(getAllCityAreasInitialize, (state) => {
        state.isFetchingCityAreas = true;
      })
      .addCase(getAllCityAreasError, (state) => {
        state.isFetchingCityAreas = false;
      })
      .addCase(getAllCityAreasSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingCityAreas: false,
        });
        const CityAreas = getRequestData(action);
        return updateDtoList(newState, 'all', CityAreas);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const CityAreas = data.filter(
          (include) => include.type === CITYAREA_INCLUDE,
        );
        return updateDtoList(state, 'all', CityAreas);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const CityAreas = data.filter(
          (include) => include.type === CITYAREA_INCLUDE,
        );
        return updateDtoList(state, 'all', CityAreas);
      })
      .addCase(updateCityAreaSuccess, (state, action) => {
        const CityArea = getRequestData(action);
        return updateDtoList(state, 'all', CityArea);
      })
      .addCase(deleteCityAreaSuccess, (state, action) => {
        const CityAreaId = getInitActionPayload(action);
        return removeItemFromList(state, 'all', CityAreaId);
      })
      .addCase(createCityAreaSuccess, (state, action) => {
        const CityArea = getRequestData(action);
        return updateDtoList(state, 'all', CityArea);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const CityAreas = data.filter(
          (include) => include.type === CITYAREA_INCLUDE,
        );
        return updateDtoList(state, 'all', CityAreas);
      });
  },
);

// -------- Sagas ---------
export function* cityAreaSaga() {
  yield all([
    fork(getAllCityAreasSaga),
    fork(updateCityAreaSaga),
    fork(deleteCityAreaSaga),
    fork(createCityAreaSaga),
  ]);
}

function* getAllCityAreasSaga() {
  yield takeEvery(
    getAllCityAreasInitialize.type,
    generateGetAllApiCall(CityAreaConnector, function* (connector) {
      return yield call([connector, connector.getCityAreas]);
    }),
  );
}

function* updateCityAreaSaga() {
  yield takeEvery(
    updateCityAreaInitialize.type,
    generateUpdateApiCall(CityAreaConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateCityArea],
        action.payload.cityAreaId,
        requestData,
      );
    }),
  );
}

function* deleteCityAreaSaga() {
  yield takeEvery(
    deleteCityAreaInitialize.type,
    generateUpdateApiCall(CityAreaConnector, function* (connector, action) {
      return yield call([connector, connector.deleteCityArea], action.payload);
    }),
  );
}

function* createCityAreaSaga() {
  yield takeEvery(
    createCityAreaInitialize.type,
    generateCreateApiCall(CityAreaConnector, function* (connector, action) {
      return yield call([connector, connector.createCityArea], action.payload);
    }),
  );
}
// -------- Selectors --------
const selectGetAllCityAreas = (state) =>
  selectAllFromApiState(state, 'cityArea');
const allCityAreasSelector = createSelector(
  selectGetAllCityAreas,
  (cityAreas) => cityAreas,
);
const allCityAreaWithNumberOfHearingsSelector = createSelector(
  [allCityAreasSelector, allHearingsSelector],
  (cityAreas, hearings) => {
    const result = [];
    cityAreas.forEach((cityArea) => {
      let amount = 0;

      hearings.forEach((hearing) => {
        if (getRelId(hearing, 'cityArea') === getEntityId(cityArea)) {
          amount += 1;
        }
      });

      const newCityArea = Object.assign({}, cityArea, {
        attributes: { ...cityArea.attributes, numOfHearings: amount },
        relationships: { ...cityArea.relationships },
      });
      result.push(newCityArea);
    });
    return result;
  },
);
const cityAreaByHearingIdSelector = (hearingId) =>
  createSelector(
    [selectGetAllCityAreas, hearingByIdSelector(hearingId)],
    (cityAreas, hearing) =>
      cityAreas.find((s) => getEntityId(s) === getRelId(hearing, 'cityArea')),
  );

const cityAreasByHearingIdsSelector = (hearingIds) =>
  createSelector(
    [allCityAreasSelector, hearingsByIdsSelector(hearingIds)],
    (cityAreas, hearings) =>
      cityAreas.filter((cityArea) =>
        hearings.some(
          (hearing) => getRelId(hearing, 'cityArea') === getEntityId(cityArea),
        ),
      ),
  );

const cityAreaByIdSelector = (cityAreaId) =>
  createSelector(allCityAreasSelector, (cityAreas) =>
    cityAreas.find((cityArea) => getEntityId(cityArea) === cityAreaId),
  );

const numberOfHearingsOnCityAreaByCityAreaIdSelector = (cityAreaId) =>
  createSelector(allHearingsSelector, (hearings) => {
    if (!cityAreaId) {
      return null;
    }
    var result = 0;
    hearings.forEach((hearing) => {
      if (getRelId(hearing, 'cityArea') === cityAreaId) {
        result += 1;
      }
    });
    return result;
  });

const cityAreaState = (state) => state.api.cityArea;

const isFetchingCityAreasSelector = createSelector(
  cityAreaState,
  (state) => state.isFetchingCityAreas,
);
// -------- Exporting --------
export {
  allCityAreasSelector,
  cityAreasByHearingIdsSelector,
  cityAreaByHearingIdSelector,
  allCityAreaWithNumberOfHearingsSelector,
  cityAreaByIdSelector,
  numberOfHearingsOnCityAreaByCityAreaIdSelector,
  isFetchingCityAreasSelector,
};
export default cityAreaReducer;
